import React, { useState } from 'react';
import RatingStars from './RatingStars';
import './ReviewForm.css';

/**
 * ReviewForm Component
 * Form for submitting product reviews
 *
 * Props:
 * - productId: number
 * - onSubmit: function(reviewData) - callback when review is submitted
 * - onCancel: function - callback when user cancels
 */
const ReviewForm = ({ productId, onSubmit, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [orderId, setOrderId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (rating === 0) {
      newErrors.rating = 'Please select a rating';
    }

    if (title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }

    if (comment.trim().length < 20) {
      newErrors.comment = 'Review must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    const reviewData = {
      product_id: productId,
      rating,
      title: title.trim(),
      comment: comment.trim(),
      order_id: orderId || null
    };

    try {
      await onSubmit(reviewData);
      // Reset form
      setRating(0);
      setTitle('');
      setComment('');
      setOrderId('');
      setErrors({});
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to submit review' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="review-form" onSubmit={handleSubmit}>
      <h3>Write a Review</h3>

      <div className="form-group">
        <label htmlFor="rating">
          Your Rating <span className="required">*</span>
        </label>
        <RatingStars
          rating={rating}
          interactive={true}
          onRatingChange={setRating}
          size="large"
        />
        {errors.rating && <span className="error-message">{errors.rating}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="title">
          Review Title <span className="required">*</span>
        </label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Sum up your experience in one line"
          maxLength="100"
          className={errors.title ? 'input-error' : ''}
        />
        <span className="char-count">{title.length}/100</span>
        {errors.title && <span className="error-message">{errors.title}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="comment">
          Your Review <span className="required">*</span>
        </label>
        <textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your thoughts about this product (minimum 20 characters)"
          rows="6"
          maxLength="2000"
          className={errors.comment ? 'input-error' : ''}
        />
        <span className="char-count">{comment.length}/2000</span>
        {errors.comment && <span className="error-message">{errors.comment}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="orderId">
          Order ID (Optional)
          <span className="hint">Include your order ID to verify your purchase</span>
        </label>
        <input
          type="text"
          id="orderId"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="e.g., 12345"
        />
      </div>

      {errors.submit && (
        <div className="error-banner">
          {errors.submit}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="button-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="button-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>

      <p className="form-note">
        Your review will be reviewed by our team before being published.
      </p>
    </form>
  );
};

export default ReviewForm;
